import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import Cookies from 'js-cookie';
import axiosInstance from '../interceptors/axios';
import { useToast } from './Toast';

const WorkoutTooltip = ({ workout }) => {
  const [show, setShow] = useState(false);
  const [flipped, setFlipped] = useState(false);
  const ref = useRef(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (show && tooltipRef.current) {
      const rect = tooltipRef.current.getBoundingClientRect();
      setFlipped(rect.top < 0);
    }
  }, [show]);

  if (!workout) return null;

  return (
    <span
      ref={ref}
      style={{ position: 'relative', display: 'inline-block', cursor: 'pointer' }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => { setShow(false); setFlipped(false); }}
      onClick={() => setShow(!show)}
    >
      <span className="rb-workout-badge">WOD</span>
      {show && (
        <div
          ref={tooltipRef}
          className={`rb-workout-tooltip ${flipped ? 'rb-workout-tooltip--below' : 'rb-workout-tooltip--above'}`}
        >
          <div className={`rb-workout-arrow ${flipped ? 'rb-workout-arrow--below' : 'rb-workout-arrow--above'}`} />
          {workout}
        </div>
      )}
    </span>
  );
};

const DashboardCalendar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast, ToastContainer } = useToast();

  const [date, setDate] = useState();
  const [displayedDate, setDisplayedDate] = useState();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);

  const handleDateChange = (selectedDate) => {
    const offset = selectedDate.getTimezoneOffset();
    const localDate = new Date(selectedDate.getTime() - (offset * 60 * 1000));
    const formattedDate = localDate.toISOString().split('T')[0];
    setDate(formattedDate);
    navigate(`/dashboard/book-class?date=${formattedDate}`);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const dateFromURL = params.get('date');

    if (dateFromURL) {
      const selectedDate = new Date(dateFromURL);
      const offset = selectedDate.getTimezoneOffset();
      const localDate = new Date(selectedDate.getTime() - (offset * 60 * 1000));
      const formattedDate = localDate.toISOString().split('T')[0];
      setDate(formattedDate);
      fetchClasses(selectedDate);
    } else {
      const selectedDate = new Date();
      const offset = selectedDate.getTimezoneOffset();
      const localDate = new Date(selectedDate.getTime() - (offset * 60 * 1000));
      const formattedDate = localDate.toISOString().split('T')[0];
      setDate(formattedDate);
      fetchClasses(localDate);
    }
  }, [location.search]);

  const fetchClasses = async (selectedDate) => {
    const formattedDate = selectedDate.toISOString().split('T')[0];
    setLoading(true);

    try {
      const requestData = { date: formattedDate, regybox_token: Cookies.get('regybox_token') };
      const response = await axiosInstance.get(`/get-classes`, { params: requestData });

      if (response.data.success === false) {
        setErrorMessages([response.data.message]);
        return;
      }

      setErrorMessages([]);
      const hiddenNames = ['pilates', 'booty'];
      const filtered = response.data.filter(c =>
        !hiddenNames.some(h => (c.class_name || '').toLowerCase().includes(h))
      );
      setClasses(filtered);
      setDisplayedDate(formattedDate);
    } catch (error) {
      setClasses([]);
      setDisplayedDate();
      if (error.response.data.message[0] === undefined) {
        setErrorMessages(['An error occurred. Please try again later.']);
      } else {
        setErrorMessages(error.response.data.message[0]);
      }
    } finally {
      setLoading(false);
    }
  };

  const joinClass = async (classe_date, classe_hour, class_id, class_name) => {
    try {
      const formData = { date: classe_date, time: classe_hour, class_id, class_name };
      const response = await axiosInstance.post(`/add-class`, formData);
      if (response.data.success === true) {
        // Show the real outcome (enrolled / waitlisted / full-retrying), then refresh.
        showToast(response.data.message?.[0] || 'Done!');
        setTimeout(() => window.location.reload(), 1200);
      } else {
        showToast(response.data.message?.[0] || 'Failed to schedule class');
      }
    } catch (error) {
      const msg = error.response?.data?.message?.[0] || 'Failed to schedule class';
      showToast(msg);
    }
  };

  const addRecurring = async (classe_date, classe_hour, class_name) => {
    try {
      const weekday = new Date(classe_date).getDay();
      const pythonWeekday = weekday === 0 ? 6 : weekday - 1;
      const formData = { weekday: pythonWeekday, time: classe_hour, class_name };
      const response = await axiosInstance.post(`/add-recurring-class`, formData);
      if (response.data.success === true) {
        window.location.reload();
      } else {
        showToast(response.data.message?.[0] || 'Failed to add recurring schedule');
      }
    } catch (error) {
      const msg = error.response?.data?.message?.[0] || 'Failed to add recurring schedule';
      showToast(msg);
    }
  };

  const removeRecurring = async (classe_date, classe_hour, class_name) => {
    try {
      const weekday = new Date(classe_date).getDay();
      const pythonWeekday = weekday === 0 ? 6 : weekday - 1;
      const formData = { weekday: pythonWeekday, time: classe_hour, class_name };
      const response = await axiosInstance.post(`/remove-recurring-class`, formData);
      if (response.data.success === true) {
        window.location.reload();
      } else {
        showToast(response.data.message?.[0] || 'Failed to remove recurring schedule');
      }
    } catch (error) {
      const msg = error.response?.data?.message?.[0] || 'Failed to remove recurring schedule';
      showToast(msg);
    }
  };

  const removeClass = async (classe_date, classe_hour) => {
    try {
      const formData = { date: classe_date, time: classe_hour };
      const response = await axiosInstance.post(`/remove-class`, formData);
      if (response.data.success === true) {
        window.location.reload();
      } else {
        showToast(response.data.message?.[0] || 'Failed to remove class');
      }
    } catch (error) {
      const msg = error.response?.data?.message?.[0] || 'Failed to remove class';
      showToast(msg);
    }
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  };

  return (
    <div className="rb-calendar-layout">
      <ToastContainer />
      {/* Sidebar: Calendar */}
      <div className="rb-calendar-sidebar">
        <Calendar
          onChange={handleDateChange}
          value={date}
        />
        {displayedDate && (
          <p className="rb-calendar-date-label">
            {formatDisplayDate(displayedDate)}
          </p>
        )}
      </div>

      {/* Main: Class cards */}
      <div>
        {loading ? (
          <div className="rb-loading">
            <div className="rb-spinner"></div>
            <p className="rb-loading-text">Loading classes...</p>
          </div>
        ) : errorMessages.length > 0 ? (
          <div className="rb-error">{errorMessages}</div>
        ) : classes.length > 0 ? (
          <div className="rb-classes-grid">
            {classes.map((classItem, index) => {
              const startHour = classItem.time ? classItem.time.split(" - ")[0] : "";
              const isOffPeak = startHour < "08:30" || startHour >= "16:00";
              const isFull = !classItem.can_join_class && !classItem.already_booked && !classItem.enrolled_for_the_day;
              const total = parseInt(classItem.total_students) || 0;
              const current = parseInt(classItem.students_in_class) || 0;

              let cardClass = 'rb-class-card';
              if (classItem.already_booked) cardClass += ' rb-class-card--booked';
              else if (isFull) cardClass += ' rb-class-card--full';
              else if (isOffPeak) cardClass += ' rb-class-card--offpeak';

              return (
                <div key={index} className={cardClass}>
                  {/* Header */}
                  <div>
                    {classItem.class_name && (
                      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px' }}>
                        <span className="rb-class-name">{classItem.class_name}</span>
                        {classItem.room && <span className="rb-class-room">{classItem.room}</span>}
                        <WorkoutTooltip workout={classItem.workout} />
                      </div>
                    )}
                  </div>

                  {/* Meta */}
                  <div className="rb-class-meta">
                    <div className="rb-class-meta-row">
                      <span className="rb-class-meta-icon">&#9201;</span>
                      <span>{classItem.time}</span>
                    </div>
                    <div className="rb-class-meta-row">
                      <span className="rb-class-meta-icon">&#9679;</span>
                      <span className={`rb-class-capacity ${current >= total ? 'rb-class-capacity--full' : 'rb-class-capacity--available'}`}>
                        {current}/{total} {current >= total ? 'Full' : 'spots'}
                      </span>
                      {classItem.queue_position && classItem.queue_position > total && (
                        <span className="rb-queue-position rb-queue-position--waitlist">
                          #{classItem.queue_position} in queue
                        </span>
                      )}
                      {classItem.queue_position && classItem.queue_position <= total && (
                        <span className="rb-queue-position rb-queue-position--assured">
                          #{classItem.queue_position} assured
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div>
                    {classItem.date < new Date().toISOString().split("T")[0] ||
                     (classItem.date === new Date().toISOString().split("T")[0] &&
                      classItem.time.split(" - ")[1] <= new Date().toTimeString().slice(0, 5)) ? (
                      null
                    ) : classItem.already_booked ? (
                      <button className="rb-btn rb-btn--info rb-btn--full rb-btn--sm" disabled>
                        Booked
                      </button>
                    ) : classItem.enrolled ? (
                      <div className="rb-btn-group">
                        <button
                          className="rb-btn rb-btn--warning rb-btn--sm"
                          onClick={() => removeClass(classItem.date, classItem.time)}
                        >
                          Scheduled
                        </button>
                        {classItem.recurring_enrolled ? (
                          <button
                            className="rb-btn rb-btn--info rb-btn--sm"
                            onClick={() => removeRecurring(classItem.date, classItem.time, classItem.class_name)}
                          >
                            Recurring
                          </button>
                        ) : (
                          <button
                            className="rb-btn rb-btn--info-outline rb-btn--sm"
                            onClick={() => addRecurring(classItem.date, classItem.time)}
                          >
                            Make Weekly
                          </button>
                        )}
                      </div>
                    ) : classItem.recurring_enrolled ? (
                      <button
                        className="rb-btn rb-btn--info rb-btn--full rb-btn--sm"
                        onClick={() => removeRecurring(classItem.date, classItem.time, classItem.class_name)}
                      >
                        Recurring &mdash; Remove
                      </button>
                    ) : classItem.enrolled_for_the_day ? (
                      <button className="rb-btn rb-btn--danger rb-btn--full rb-btn--sm" disabled>
                        Already enrolled today
                      </button>
                    ) : (
                      <div className="rb-btn-group">
                        <button
                          className="rb-btn rb-btn--success rb-btn--sm"
                          onClick={() => joinClass(classItem.date, classItem.time, classItem.class_id, classItem.class_name)}
                        >
                          Enroll now
                        </button>
                        <button
                          className="rb-btn rb-btn--info-outline rb-btn--sm"
                          onClick={() => addRecurring(classItem.date, classItem.time, classItem.class_name)}
                        >
                          Weekly
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="rb-empty">
            <div className="rb-empty-icon">&#128197;</div>
            <p>No classes available for this day</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardCalendar;
